<template>
  <div class="shipping_progress_bar">  
    <div class="progress_bar_wrapper">      

      <div class="cash" :class="{ _flying: flying }" :style="{ transform: `translateX(${ animatedProgressOverride !== false ? progressToPx(animatedProgressOverride) : progressToPx(progressData) })` }" :key="updateSwitch">
        <div :class="{ _pulse: celebrating }">
          <div :class="{ _shimmy: celebrating }">
            <img :class="iconType === 'gwp' ? '_wad _wf_bag' : '_wad'" :src="iconType === 'gwp' ? theme.images.wf_bag : theme.images.cash_wad" role="presentation">
            <span v-if="iconType !== 'gwp'" class="_left_wing">
              <img class="" :src="theme.images.cash_left_wing" role="presentation">
            </span>
            <span v-if="iconType !== 'gwp'" class="_right_wing">
              <img class="" :src="theme.images.cash_right_wing" role="presentation">
            </span>
          </div>
        </div>
      </div>

      <div class="progress_bar">
        <div class="progress_bar_fill" :style="{ transform: `translateX(${ animatedProgressOverride !== false ? animatedProgressOverride : progressData }%)` }"></div>
      </div>
    </div>
    <div class="shipping_bar_message" v-html="message"></div>
  </div>
</template>

<script>
  // The celebrating animations are shorter than this, but we want a slight wait for the effect to settle.
  const celebratingDuration = 1000;

  export default {
    name: 'shipping_progress_bar',
    props: ['id', 'currentTier', 'prevTier', 'cartTotal', 'shown', 'delay', 'iconType'],
    computed: {
      message: function() {
        const { formatMoney } = this.$options.filters;

        const { currentTier, cartTotal, prevTier } = this;
        const { min_spend: total, progress_message, subtitle } = currentTier;
        let totalCents = (total * 100);

        const subtitleHtml = subtitle ? `<span class="shipping_bar_subtitle">${ subtitle }</span>` : '';

        // Cart total is already in the current currency
        let wipCartTotal = cartTotal;
        let message = '';

        if (prevTier) {
          const { min_spend: prevTotal, achieved_message } = prevTier;
          let prevTotalCents = (prevTotal * 100);
          totalCents -= prevTotalCents;
          wipCartTotal -= prevTotalCents;
          message += `${ achieved_message } `;
        }

        if (prevTier === currentTier || this.achieving) {
          return `${ message }${ subtitleHtml }`;
        }

        const diff = Math.max(0, totalCents - wipCartTotal);

        const progressMessage = progress_message.replaceAll('[diff]', formatMoney(diff));

        message += progressMessage;
        return `${ message }${ subtitleHtml }`;
      },
      progress: function() {
        const { formatMoney } = this.$options.filters;
        
        const { currentTier, cartTotal, prevTier } = this;
        const { min_spend: total, progress_message } = currentTier;
        let totalCents = (total * 100);

        let wipCartTotal = cartTotal;

        if (prevTier && prevTier.min_spend !== currentTier.min_spend) {
          const { min_spend: prevTotal } = prevTier;
          let prevTotalCents = (prevTotal * 100);
          totalCents -= prevTotalCents;
          wipCartTotal -= prevTotalCents;
        }

        const progressValue = totalCents > 0 ? Math.min(100, (wipCartTotal / totalCents) * 100) : 100;
        
        return progressValue;
      },
      achievedHighestTier: function() {
        const { currentTier, prevTier } = this;
        // Total should be unique, otherwise it'd cause all sorts of issues anyway.
        return prevTier.min_spend === currentTier.min_spend;
      },
    },
    methods: {
      fly: function() {
        const { id } = this;
        this.flying = true;
        const timeoutTitle = `flyingTimeout:${ id }`;
        if (window[timeoutTitle]) {
          clearTimeout(window[timeoutTitle]);
        }
        window[timeoutTitle] = setTimeout(() => this.flying = false, 2000);
      },
      celebrate: function() {
        const { id } = this;
        this.celebrating = true;
        const timeoutTitle = `celebratingTimeout:${ id }`;
        if (window[timeoutTitle]) {
          clearTimeout(window[timeoutTitle]);
        }
        window[timeoutTitle] = setTimeout(() => this.celebrating = false, celebratingDuration);
      },
      progressToPx: function(progress) {
        // move progress back by half the cash width
        const adjustment = -15;
        const cash_width = 30;

        const { wrapper } = this;
        if (!wrapper) {
          return;
        }

        const width = wrapper.clientWidth;
        const unclamped = (progress / 100 * width) + adjustment;
        // Fill runs flush to the track edge; allow a few px past the wrapper so the icon lines up visually.
        const maxOverhangPx = 6;
        // Keep min at adjustment (half cash width): clamping to 0 shifted the wad right vs centered-on-start.
        const clamped = Math.min(Math.max(unclamped, adjustment), width - cash_width + maxOverhangPx);
        return `${ clamped }px`;
      },
    },
    data() {
      return {
        flying: false,
        celebrating: false,
        achieving: false,
        wrapper: false,
        progressData: 0,
        animatedProgressOverride: false,
        messageData: false,
        updateSwitch: false,
      }
    },
    watch: { 
      cartTotal: function(newVal, oldVal) {
        // console.log('cartTotalChange', newVal);
        this.progressData = this.shown ? this.progress : 0;
      },
      progress: function(newVal, oldVal) {
        this.progressData = this.shown ? newVal : 0;
      },
      shown: function(newVal, oldVal) {
        // console.log('shownChange', newVal);
        const { delay, progress } = this;
        setTimeout(() => {
          this.progressData = newVal ? this.progress : 0;
        }, delay);
      },
      progressData: function(newVal, oldVal) {
        // console.log('progressDataChange', newVal);
        this.fly();
      },
      prevTier: function(newVal, oldVal) {
        // console.log('prevTierChange', newVal);
        const { min_spend: newTotal = 0 } = newVal || {};
        const { min_spend: oldTotal = 0 } = oldVal || {};

        if (newTotal > oldTotal) {
          // Customer has achieved a new tier
          // Success animation
          this.achieving = true;
          const { wrapper, prevTier, currentTier, achievedHighestTier } = this;
          const cash = wrapper.querySelector('.cash');
          const progressBar = wrapper.querySelector('.progress_bar_fill');

          this.animatedProgressOverride = 100;
          setTimeout(() => {
            this.celebrate();

            // Reset progress bar
            if (achievedHighestTier) {
              this.animatedProgressOverride = false;
              this.achieving = false;
              return;
            }

            setTimeout(() => {
              cash.style.transition = 'unset';
              progressBar.style.transition = 'unset';
              this.animatedProgressOverride = 0;
              this.achieving = false;

              setTimeout(() => {
                cash.style.transition = null;
                progressBar.style.transition = null;
                this.animatedProgressOverride = false;
                this.fly();
              }, 500);
            }, celebratingDuration);

          }, 2000);
        }
      },
    },
    mounted() {
      const { id } = this;
      this.wrapper = document.getElementById(id);
      if (this.shown) {
        this.progressData = this.progress;
      }

      let updateTimeout;

      const forceUpdate = () => {
        this.updateSwitch = !this.updateSwitch;
      }

      window.addEventListener('resize', e => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(forceUpdate, 100);
      });
    },
  }
</script>